import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle } from 'lucide-react';
import ActionsHeaderHelp from '../../../components/ActionsHeaderHelp';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import EmptyState from '../../../components/EmptyState';
import RichContent from '../../../components/RichContent';
import {
  StandardTable, StandardTableBody, StandardTableCell, StandardTableHeader,
  StandardTableHeaderCell, StandardTableRow,
} from '../../../components/StandardTable';
import { AddIcon, DeleteIcon, DownloadIcon, EditIcon, OpenExternalIcon, UploadIcon } from '../../../components/icons';
import { HelpTrigger } from '../../../help';
import AdminModal from '../../components/AdminModal';
import EndorsementList from '../../components/endorsements/EndorsementList';
import { endorserDisplayName } from '../../../constants/endorsements';
import { formatDate } from '../../lib/format';
import { provenanceLabel } from '../../../constants/ballotInitiatives';
import {
  PROFILE_FIELD_LABELS, RESPONSE_STATUS_LABELS, STANCE_LABELS, POSITION_SOURCE_LABELS, coverageLabel,
} from '../../../constants/candidateProfiles';
import type { Stance } from '../../../constants/candidateProfiles';
import { apiError } from '../elections/InitiativeDetailPage';
import { formatFileSize } from '../elections/InitiativeDocumentsCard';
import {
  documentDownloadUrl, fetchCandidateProfile, profileFieldValues, setProfilePublication,
  type CandidateEndorsement, type CandidatePosition, type CandidateStatement, type EditMarker, type ProfileRowKind,
  type ProvenanceEntry,
} from '../../lib/candidateProfiles';
import CandidateProfileImportModal from './CandidateProfileImportModal';
import {
  DeleteRowModal, EndorsementModal, PositionModal, ProfileEditModal, StatementModal,
} from './CandidateProfileEditors';

interface Props {
  candidateId: string;
}

export const profileQueryKey = (candidateId: string) => ['admin-people', 'profile', candidateId];

const STANCE_TONE: Record<Stance, 'positive' | 'negative' | 'neutral' | 'caution' | 'info'> = {
  support: 'positive',
  oppose: 'negative',
  unclear: 'neutral',
  declined: 'caution',
  no_position: 'neutral',
};

// Staff-written carries no public label (unlabelled = our team wrote it); the
// admin view names it so a hand edit is visible next to imported text.
const provenanceText = (entry: ProvenanceEntry | undefined): string | null =>
  entry?.origin === 'staff_written'
    ? 'Staff-written'
    : provenanceLabel(entry ? { origin: entry.origin, tool: entry.tool ?? undefined, source: entry.source ?? undefined } : undefined);

/** Statements grouped by their source, in display order (§5.4.1). */
const groupStatements = (statements: CandidateStatement[]) => {
  const groups = new Map<string, { source_name: string; source_url: string | null; source_date: string | null; rows: CandidateStatement[] }>();
  for (const s of statements) {
    const key = `${s.source_name}|${s.source_date ?? ''}`;
    if (!groups.has(key)) groups.set(key, { source_name: s.source_name, source_url: s.source_url, source_date: s.source_date, rows: [] });
    groups.get(key)!.rows.push(s);
  }
  return [...groups.values()];
};

const cardHeading = 'text-sm font-semibold text-text-primary dark:text-text-dark-primary';
const muted = 'text-xs text-text-tertiary dark:text-text-dark-tertiary';

/** "Edited by hand by Angie · Sep 28, 2026" for a row added or corrected here. */
const EditedNote: React.FC<{ marker: EditMarker | undefined }> = ({ marker }) =>
  marker ? (
    <span className={`block italic ${muted}`}>
      Edited by hand{marker.updated_by_name ? ` by ${marker.updated_by_name}` : ''} · {formatDate(marker.updated_at)}
    </span>
  ) : null;

/** Edit + remove icon buttons for one row. */
const RowActions: React.FC<{ label: string; onEdit: () => void; onDelete: () => void }> = ({ label, onEdit, onDelete }) => (
  <span className="flex shrink-0 items-center gap-1">
    <Button variant="ghost" size="sm" icon={EditIcon} ariaLabel={`Edit ${label}`} onClick={onEdit} />
    <Button variant="ghost" size="sm" icon={DeleteIcon} ariaLabel={`Remove ${label}`} onClick={onDelete} />
  </span>
);

/** The legend for every row-actions column on these cards. */
const ROW_ACTION_ICONS = [
  { key: 'edit' as const, note: 'Corrects this row by hand; it is marked as edited, and the next bundle import warns before replacing it.' },
  { key: 'delete' as const, note: 'Removes this row. Re-importing the bundle brings it back.' },
];

/** Which row editor is open: undefined = closed, `row: null` = adding. */
type Editing<Row> = { row: Row | null } | undefined;

type RemoveTarget = { kind: ProfileRowKind; id: string; label: string };

/**
 * The candidate-profile cards on a candidacy (CANDIDATE_PROFILES_PRD.md
 * §5.4.1): Citizen Profile (six fields with provenance, tier, publication,
 * Import, Edit and Publish), In Their Own Words, Endorsements, Positions on
 * ballot measures, Supporting Documents. Everything shown here is the admin
 * view — drafts included; the reader surfaces show only what is published.
 * Every block is editable by hand (v1.7, O1 revisited): the import is the bulk
 * path, these editors the correction path.
 */
const CandidateProfileCards: React.FC<Props> = ({ candidateId }) => {
  const queryClient = useQueryClient();
  const [importOpen, setImportOpen] = useState(false);
  const [confirm, setConfirm] = useState<'publish' | 'unpublish' | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingStatement, setEditingStatement] = useState<Editing<CandidateStatement>>(undefined);
  const [editingEndorsement, setEditingEndorsement] = useState<Editing<CandidateEndorsement>>(undefined);
  const [editingPosition, setEditingPosition] = useState<Editing<CandidatePosition>>(undefined);
  const [removing, setRemoving] = useState<RemoveTarget | null>(null);

  const view = useQuery({
    queryKey: profileQueryKey(candidateId),
    queryFn: () => fetchCandidateProfile(candidateId),
    enabled: candidateId !== '',
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: profileQueryKey(candidateId) });
    queryClient.invalidateQueries({ queryKey: ['admin-people', 'campaign', candidateId] });
    queryClient.invalidateQueries({ queryKey: ['admin-people', 'profile-measures', candidateId] });
  };

  const publication = useMutation({
    mutationFn: (status: 'published' | 'draft') => setProfilePublication(candidateId, status),
    onSuccess: (result) => {
      toast.success(result.publication_status === 'published' ? 'Profile published — live for citizens now' : 'Profile unpublished');
      setConfirm(null);
      refresh();
    },
    onError: (err: any) => toast.error(apiError(err, 'Could not change publication')),
  });

  const download = async (documentId: string) => {
    try {
      const { download_url } = await documentDownloadUrl(candidateId, documentId);
      window.open(download_url, '_blank', 'noopener');
    } catch (err: any) {
      toast.error(apiError(err, 'Could not prepare the download'));
    }
  };

  if (view.isLoading) {
    return <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">Loading profile…</p>;
  }
  if (view.isError) {
    return (
      <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
        <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
        <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">Couldn&apos;t load the candidate profile.</p>
        <Button variant="outline" size="sm" onClick={() => view.refetch()}>Retry</Button>
      </div>
    );
  }

  const data = view.data;
  const profile = data?.profile ?? null;
  const published = profile?.publication_status === 'published';
  const edits = data?.edits;
  const nextOrder = (data?.statements ?? []).reduce((max, s) => Math.max(max, s.display_order + 1), 0);
  const measureName = (p: CandidatePosition) => p.measure_number ?? p.popular_name ?? p.ballot_title ?? 'this measure';

  return (
    <>
      {/* Citizen Profile */}
      <div className="card p-5">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="flex flex-wrap items-center gap-2">
            <h4 className={cardHeading}>Citizen profile</h4>
            <Chip label={coverageLabel(profile?.coverage_tier)} tone={profile ? 'info' : 'neutral'} size="sm" />
            {profile && (
              <Chip label={published ? 'Published' : 'Draft — not visible to citizens'} tone={published ? 'positive' : 'caution'} size="sm" />
            )}
            {profile && (
              <HelpTrigger contentKey="candidate-profile-publication" size="sm" ariaLabel="Help for publishing a candidate profile" />
            )}
          </span>
          <span className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" icon={UploadIcon} onClick={() => setImportOpen(true)}>
              Import bundle
            </Button>
            <Button variant="secondary" size="sm" icon={profile ? EditIcon : AddIcon} onClick={() => setEditingProfile(true)}>
              {profile ? 'Edit' : 'Start profile'}
            </Button>
            {profile && !published && (
              <Button variant="primary" size="sm" onClick={() => setConfirm('publish')}>Publish</Button>
            )}
            {profile && published && (
              <Button variant="outline" size="sm" onClick={() => setConfirm('unpublish')}>Unpublish</Button>
            )}
          </span>
        </div>

        {data?.running_mate && (
          <p className={`mb-3 ${muted}`}>
            {data.running_mate.role === 'governor' ? 'Runs with' : 'Running mate'}:{' '}
            <Link to={`/admin-next/people/${data.running_mate.person_id}`} className="underline">{data.running_mate.name}</Link>
            {data.running_mate.role === 'governor' ? ' for Governor' : ' for Lt. Governor'}
          </p>
        )}

        {!profile ? (
          <EmptyState
            size="compact"
            title="Official record only"
            hint="No profile for this candidacy yet. Citizens see the office, party, committee and record; import the skill's bundle, or start a profile by hand, to add the six fields, their own words, endorsements and measure positions."
          />
        ) : (
          <>
            <dl className="mb-4 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
              <dt className="text-text-secondary dark:text-text-dark-secondary">Version</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">{profile.profile_version ?? '—'}</dd>
              <dt className="text-text-secondary dark:text-text-dark-secondary">Facts as of</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">{profile.facts_as_of ? formatDate(profile.facts_as_of) : '—'}</dd>
              <dt className="text-text-secondary dark:text-text-dark-secondary">Published</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">{profile.published_at ? formatDate(profile.published_at) : '—'}</dd>
              <dt className="text-text-secondary dark:text-text-dark-secondary">Updated</dt>
              <dd className="text-text-primary dark:text-text-dark-primary">{formatDate(profile.updated_at)}</dd>
            </dl>
            <div className="space-y-4">
              {profileFieldValues(profile).map(([field, html]) => {
                const label = provenanceText(profile.content_provenance?.[field]);
                return (
                  <div key={field}>
                    <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-secondary dark:text-text-dark-secondary">
                      {PROFILE_FIELD_LABELS[field]}
                    </h5>
                    {html ? (
                      <RichContent html={html} className="text-sm text-text-primary dark:text-text-dark-primary" />
                    ) : (
                      <p className={muted}>Empty</p>
                    )}
                    {label && <p className={`mt-0.5 italic ${muted}`}>{label}</p>}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* In Their Own Words */}
      <div className="card p-5">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h4 className={cardHeading}>In their own words</h4>
          <Button variant="ghost" size="sm" icon={AddIcon} onClick={() => setEditingStatement({ row: null })}>Add</Button>
        </div>
        {!data || data.statements.length === 0 ? (
          <p className={muted}>No questionnaire rows — the bundle carries one row per question in the race&apos;s questionnaire set, including dated non-responses.</p>
        ) : (
          <div className="space-y-4">
            {groupStatements(data.statements).map((group) => (
              <div key={`${group.source_name}|${group.source_date ?? ''}`}>
                <p className="mb-1 text-xs font-semibold text-text-secondary dark:text-text-dark-secondary">
                  {group.source_url ? (
                    <a href={group.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline">
                      {group.source_name} <OpenExternalIcon className="h-3 w-3" />
                    </a>
                  ) : group.source_name}
                  {group.source_date ? ` · ${formatDate(group.source_date)}` : ''}
                </p>
                <ul className="space-y-2">
                  {group.rows.map((row) => (
                    <li key={row.id} className="flex items-start justify-between gap-2 text-sm">
                      <div className="min-w-0 flex-1">
                        {row.question && <p className="font-medium text-text-primary dark:text-text-dark-primary">{row.question}</p>}
                        {row.response_status === 'answered' ? (
                          <blockquote className="mt-1 whitespace-pre-line border-l-2 border-border-light pl-3 text-text-secondary dark:border-border-dark dark:text-text-dark-secondary">
                            {row.body}
                          </blockquote>
                        ) : (
                          <p className={`mt-1 italic ${muted}`}>
                            {RESPONSE_STATUS_LABELS[row.response_status]}{row.source_date ? ` as of ${formatDate(row.source_date)}` : ''}
                          </p>
                        )}
                        <EditedNote marker={edits?.statements[row.id]} />
                      </div>
                      <RowActions
                        label={row.question ? `statement: ${row.question.slice(0, 60)}` : `statement from ${row.source_name}`}
                        onEdit={() => setEditingStatement({ row })}
                        onDelete={() => setRemoving({ kind: 'statements', id: row.id, label: row.question ?? `A statement from ${row.source_name}` })}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Endorsements */}
      <div className="card p-5">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h4 className={cardHeading}>Endorsements</h4>
          <Button variant="ghost" size="sm" icon={AddIcon} onClick={() => setEditingEndorsement({ row: null })}>Add</Button>
        </div>
        <EndorsementList
          rows={data?.endorsements ?? []}
          actionNotes={{ edit: ROW_ACTION_ICONS[0].note, delete: ROW_ACTION_ICONS[1].note }}
          renderNote={(e) => <EditedNote marker={edits?.endorsements[e.id]} />}
          onEdit={(e) => setEditingEndorsement({ row: e })}
          onDelete={(e) => setRemoving({ kind: 'endorsements', id: e.id, label: `The endorsement by ${endorserDisplayName(e)}` })}
        />
      </div>

      {/* Positions on ballot measures */}
      <div className="card p-5">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h4 className={cardHeading}>Positions on ballot measures</h4>
          <Button variant="ghost" size="sm" icon={AddIcon} onClick={() => setEditingPosition({ row: null })}>Add</Button>
        </div>
        {!data || data.positions.length === 0 ? (
          <p className={muted}>No rows — the bundle carries one row per statewide measure, including a dated &ldquo;No position found&rdquo;.</p>
        ) : (
          <StandardTable>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell columnType="fit">Measure</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Stance</StandardTableHeaderCell>
                <StandardTableHeaderCell>Evidence</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Source</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Date</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="actions" align="right">
                  <span className="inline-flex items-center gap-1.5">
                    Actions
                    <ActionsHeaderHelp icons={ROW_ACTION_ICONS} />
                  </span>
                </StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {data.positions.map((p) => {
                const label = STANCE_LABELS[p.stance];
                return (
                  <StandardTableRow key={p.id}>
                    <StandardTableCell>
                      <Link to={`/admin-next/elections/initiatives/${p.ballot_initiative_id}`} className="underline">
                        {measureName(p)}
                      </Link>
                      <EditedNote marker={edits?.positions[p.id]} />
                    </StandardTableCell>
                    <StandardTableCell>
                      {label ? <Chip label={label} tone={STANCE_TONE[p.stance]} size="sm" /> : <span className={muted}>Unclear — see quote</span>}
                    </StandardTableCell>
                    <StandardTableCell>
                      {p.quote ? (
                        <span className="text-xs text-text-secondary dark:text-text-dark-secondary">&ldquo;{p.quote}&rdquo;</span>
                      ) : p.source_type === 'referral_vote' ? (
                        <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Recorded vote on referring the measure</span>
                      ) : <span className={muted}>—</span>}
                    </StandardTableCell>
                    <StandardTableCell>
                      {p.source_name ? (
                        <span className="text-xs">
                          {p.source_url ? (
                            <a href={p.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline">
                              {p.source_name} <OpenExternalIcon className="h-3 w-3" />
                            </a>
                          ) : p.source_name}
                          {p.source_type ? <span className={`block ${muted}`}>{POSITION_SOURCE_LABELS[p.source_type] ?? p.source_type}</span> : null}
                        </span>
                      ) : <span className={muted}>roster checked</span>}
                    </StandardTableCell>
                    <StandardTableCell>{formatDate(p.source_date)}</StandardTableCell>
                    <StandardTableCell>
                      <RowActions
                        label={`position on ${measureName(p)}`}
                        onEdit={() => setEditingPosition({ row: p })}
                        onDelete={() => setRemoving({ kind: 'positions', id: p.id, label: `The position on ${measureName(p)}` })}
                      />
                    </StandardTableCell>
                  </StandardTableRow>
                );
              })}
            </StandardTableBody>
          </StandardTable>
        )}
      </div>

      {/* Supporting documents */}
      <div className="card p-5">
        <h4 className={`mb-2 ${cardHeading}`}>Supporting documents</h4>
        {!data || data.documents.length === 0 ? (
          <p className={muted}>No traceability records yet — importing a bundle with its record JSON and PDF files them here.</p>
        ) : (
          <StandardTable>
            <StandardTableHeader>
              <StandardTableRow>
                <StandardTableHeaderCell>Record</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Version</StandardTableHeaderCell>
                <StandardTableHeaderCell>File</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Uploaded</StandardTableHeaderCell>
                <StandardTableHeaderCell columnType="fit">Download</StandardTableHeaderCell>
              </StandardTableRow>
            </StandardTableHeader>
            <StandardTableBody>
              {data.documents.map((d) => (
                <StandardTableRow key={d.id}>
                  <StandardTableCell>
                    <div className="text-text-primary dark:text-text-dark-primary">{d.label}</div>
                    {d.notes && <div className={muted}>{d.notes}</div>}
                  </StandardTableCell>
                  <StandardTableCell>{d.version_label ?? '—'}</StandardTableCell>
                  <StandardTableCell>
                    <div className="text-xs">{d.original_name}</div>
                    <div className={muted}>{formatFileSize(d.file_size)} · sha256 {d.sha256.slice(0, 12)}…</div>
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="text-xs">{formatDate(d.uploaded_at)}</div>
                    {d.uploaded_by_name && <div className={muted}>{d.uploaded_by_name}</div>}
                  </StandardTableCell>
                  <StandardTableCell>
                    <Button variant="ghost" size="sm" icon={DownloadIcon} onClick={() => download(d.id)}>
                      Download
                    </Button>
                  </StandardTableCell>
                </StandardTableRow>
              ))}
            </StandardTableBody>
          </StandardTable>
        )}
      </div>

      <ProfileEditModal
        candidateId={candidateId}
        isOpen={editingProfile}
        profile={profile}
        onClose={() => setEditingProfile(false)}
        onSaved={refresh}
      />
      <StatementModal
        candidateId={candidateId}
        isOpen={editingStatement !== undefined}
        row={editingStatement?.row ?? null}
        nextOrder={nextOrder}
        onClose={() => setEditingStatement(undefined)}
        onSaved={refresh}
      />
      <EndorsementModal
        candidateId={candidateId}
        isOpen={editingEndorsement !== undefined}
        row={editingEndorsement?.row ?? null}
        onClose={() => setEditingEndorsement(undefined)}
        onSaved={refresh}
      />
      <PositionModal
        candidateId={candidateId}
        isOpen={editingPosition !== undefined}
        row={editingPosition?.row ?? null}
        onClose={() => setEditingPosition(undefined)}
        onSaved={refresh}
      />
      <DeleteRowModal
        candidateId={candidateId}
        target={removing}
        published={published}
        onClose={() => setRemoving(null)}
        onDeleted={refresh}
      />

      <CandidateProfileImportModal
        candidateId={candidateId}
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={refresh}
      />

      <AdminModal
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === 'publish' ? 'Publish this profile?' : 'Unpublish this profile?'}
        size="sm"
        footer={<>
          <Button variant="secondary" size="sm" onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant={confirm === 'publish' ? 'primary' : 'danger'}
            size="sm"
            loading={publication.isPending}
            onClick={() => publication.mutate(confirm === 'publish' ? 'published' : 'draft')}
          >
            {confirm === 'publish' ? 'Publish' : 'Unpublish'}
          </Button>
        </>}
      >
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {confirm === 'publish'
            ? 'The profile, statements, endorsements and measure positions go live for every citizen, on the public feed and the marketing site, within a minute — if the candidacy is on the ballot. It drops off on its own if the candidacy is withdrawn.'
            : 'The profile leaves every citizen and public surface immediately. Nothing is deleted; you can publish it again.'}
        </p>
      </AdminModal>
    </>
  );
};

export default CandidateProfileCards;
